import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    ArrowLeftIcon,
    MapPinIcon,
    CalendarIcon,
    ClockIcon,
    CurrencyDollarIcon,
    UserCircleIcon,
    PhoneIcon,
    EnvelopeIcon,
    XCircleIcon,
    CheckCircleIcon,
    StarIcon,
    TruckIcon,
    TicketIcon,
    ChatBubbleLeftEllipsisIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarSolid } from "@heroicons/react/24/solid";

import { Button } from "@/components/ui/Button";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import { usePermissions } from "@/features/auth/usePermissions";
import { getTripDetails, cancelTrip, forceCompleteTrip } from "../api/rideBookingsTripsApi";
import { extractApiError } from "@/lib/api-error";

// ============================================================================
// Badges
// ============================================================================

const StatusBadge = ({ status }: { status: string }) => {
    const s = status ?? "UNKNOWN";
    const styles: Record<string, string> = {
        PENDING: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
        STARTED: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        DRIVER_ARRIVED: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
        PICKED_UP: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
        IN_PROGRESS: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        COMPLETED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        CANCELLED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        PAID: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        REFUNDED: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        FAILED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    };
    return (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${styles[s] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            {s.replace(/_/g, " ")}
        </span>
    );
};

// ============================================================================
// Helpers
// ============================================================================

const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "—";
    try {
        return new Intl.DateTimeFormat("en-NG", {
            month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
        }).format(new Date(dateStr));
    } catch {
        return dateStr;
    }
};

const formatCurrency = (amount: number | null | undefined, currency = "NGN") => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("en-NG", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
};

const RatingStars = ({ rating }: { rating: number }) => (
    <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
            star <= rating
                ? <StarSolid key={star} className="h-4 w-4 text-amber-400" />
                : <StarIcon key={star} className="h-4 w-4 text-slate-300 dark:text-slate-600" />
        ))}
        <span className="ml-1 text-sm font-medium text-slate-700 dark:text-slate-300">{rating}</span>
    </div>
);

// ============================================================================
// Timeline Event
// ============================================================================

const TimelineEvent = ({ label, time, isLast, isActive }: { label: string; time: string | null; isLast?: boolean; isActive: boolean }) => (
    <div className="flex gap-3">
        <div className="flex flex-col items-center">
            <div className={`h-3 w-3 rounded-full border-2 ${isActive ? "border-emerald-500 bg-emerald-100" : "border-slate-300 bg-slate-100 dark:border-slate-600 dark:bg-slate-700"}`} />
            {!isLast && <div className="h-8 w-0.5 bg-slate-200 dark:bg-slate-700" />}
        </div>
        <div className="-mt-0.5">
            <p className={`text-sm font-medium ${isActive ? "text-slate-900 dark:text-slate-100" : "text-slate-400 dark:text-slate-500"}`}>{label}</p>
            <p className="text-xs text-slate-400">{time ? formatDate(time) : "—"}</p>
        </div>
    </div>
);

// ============================================================================
// Component
// ============================================================================

export const TripDetailsRoute = () => {
    const { tripId } = useParams<{ tripId: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { hasPermission } = usePermissions();
    const [cancelModal, setCancelModal] = useState(false);
    const [completeModal, setCompleteModal] = useState(false);
    const [actionReason, setActionReason] = useState("");
    const [finalFare, setFinalFare] = useState("");

    const { data, isLoading, error } = useQuery({
        queryKey: ["tripDetails", tripId],
        queryFn: () => getTripDetails(tripId!),
        enabled: !!tripId,
    });

    const cancelMutation = useMutation({
        mutationFn: (reason: string) => cancelTrip(tripId!, reason),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["tripDetails", tripId] });
            queryClient.invalidateQueries({ queryKey: ["trips"] });
            setCancelModal(false);
            setActionReason("");
        },
    });

    const completeMutation = useMutation({
        mutationFn: ({ reason, fare }: { reason: string; fare?: number }) =>
            forceCompleteTrip(tripId!, reason, fare),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["tripDetails", tripId] });
            queryClient.invalidateQueries({ queryKey: ["trips"] });
            setCompleteModal(false);
            setActionReason("");
            setFinalFare("");
        },
    });

    const trip = data?.data;

    if (isLoading) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
            </div>
        );
    }

    if (error || !trip) {
        return (
            <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4">
                <XCircleIcon className="h-12 w-12 text-rose-400" />
                <p className="text-sm text-slate-600 dark:text-slate-400">{error ? extractApiError(error) : "Trip not found"}</p>
                <Button variant="outline" onClick={() => navigate("/ride-bookings-trips/trips")}>
                    <ArrowLeftIcon className="mr-2 h-4 w-4" />Back to Trips
                </Button>
            </div>
        );
    }

    const isActive = ["PENDING", "STARTED", "DRIVER_ARRIVED", "PICKED_UP", "IN_PROGRESS"].includes(trip.status);
    const canManageTrips = hasPermission(SECTION_PERMISSIONS.TRIPS_MANAGE[0]);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate("/ride-bookings-trips/trips")}>
                        <ArrowLeftIcon className="h-5 w-5" />
                    </Button>
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Trip Details</h1>
                            <StatusBadge status={trip.status} />
                            {trip.paymentStatus && <StatusBadge status={trip.paymentStatus} />}
                        </div>
                        <p className="mt-1 font-mono text-xs text-slate-400">{trip.tripId}</p>
                    </div>
                </div>
                {canManageTrips && isActive ? (
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" className="text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400" onClick={() => setCompleteModal(true)}>
                            <CheckCircleIcon className="mr-1.5 h-4 w-4" />Force Complete
                        </Button>
                        <Button variant="outline" size="sm" className="text-rose-600 hover:bg-rose-50 dark:text-rose-400" onClick={() => setCancelModal(true)}>
                            <XCircleIcon className="mr-1.5 h-4 w-4" />Cancel Trip
                        </Button>
                    </div>
                ) : null}
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {/* Main Content */}
                <div className="space-y-6 lg:col-span-2">
                    {/* Route Card */}
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Route</h2>
                        </div>
                        <div className="space-y-4 p-6">
                            <div className="flex items-start gap-4">
                                <div className="mt-1 flex flex-col items-center gap-0.5">
                                    <div className="h-3 w-3 rounded-full border-2 border-emerald-500 bg-emerald-100" />
                                    <div className="h-10 w-0.5 bg-slate-200 dark:bg-slate-700" />
                                    <div className="h-3 w-3 rounded-full border-2 border-rose-500 bg-rose-100" />
                                </div>
                                <div className="flex-1 space-y-4">
                                    <div>
                                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Pickup</p>
                                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{trip.pickupAddress}</p>
                                        <p className="mt-0.5 font-mono text-xs text-slate-400">{trip.pickupLatitude?.toFixed(4)}, {trip.pickupLongitude?.toFixed(4)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Dropoff</p>
                                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{trip.dropoffAddress}</p>
                                        <p className="mt-0.5 font-mono text-xs text-slate-400">{trip.dropoffLatitude?.toFixed(4)}, {trip.dropoffLongitude?.toFixed(4)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Fare & Metrics */}
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Fare & Metrics</h2>
                        </div>
                        <div className="grid grid-cols-2 gap-4 p-6 sm:grid-cols-4">
                            <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Estimated Fare</p>
                                <p className="mt-1 text-xl font-bold text-slate-900 dark:text-slate-100">{formatCurrency(trip.estimatedFare, trip.estimatedFareCurrency)}</p>
                            </div>
                            <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Final Fare</p>
                                <p className="mt-1 text-xl font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(trip.finalFare, trip.finalFareCurrency)}</p>
                            </div>
                            <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Distance</p>
                                <p className="mt-1 text-xl font-bold text-slate-900 dark:text-slate-100">{trip.distanceKm !== null ? `${trip.distanceKm.toFixed(1)} km` : "—"}</p>
                            </div>
                            <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Duration</p>
                                <p className="mt-1 text-xl font-bold text-slate-900 dark:text-slate-100">{trip.durationMinutes !== null ? `${trip.durationMinutes} min` : "—"}</p>
                            </div>
                        </div>
                    </div>

                    {/* Ratings */}
                    {(trip.driverRating !== null || trip.passengerRating !== null) && (
                        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                            <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Ratings & Reviews</h2>
                            </div>
                            <div className="grid grid-cols-1 gap-6 p-6 sm:grid-cols-2">
                                {trip.driverRating !== null && (
                                    <div className="space-y-2">
                                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Rating for Driver</p>
                                        <RatingStars rating={trip.driverRating} />
                                        {trip.driverRatingComment && (
                                            <div className="flex items-start gap-2 rounded-lg bg-slate-50 p-3 dark:bg-slate-800/50">
                                                <ChatBubbleLeftEllipsisIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                                                <p className="text-sm text-slate-600 dark:text-slate-400">{trip.driverRatingComment}</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {trip.passengerRating !== null && (
                                    <div className="space-y-2">
                                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Rating for Passenger</p>
                                        <RatingStars rating={trip.passengerRating} />
                                        {trip.passengerRatingComment && (
                                            <div className="flex items-start gap-2 rounded-lg bg-slate-50 p-3 dark:bg-slate-800/50">
                                                <ChatBubbleLeftEllipsisIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                                                <p className="text-sm text-slate-600 dark:text-slate-400">{trip.passengerRatingComment}</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Cancellation Info */}
                    {trip.cancellationReason && (
                        <div className="overflow-hidden rounded-2xl border border-rose-200 bg-rose-50 shadow-sm dark:border-rose-900/50 dark:bg-rose-900/10">
                            <div className="p-6">
                                <div className="flex items-start gap-3">
                                    <XCircleIcon className="mt-0.5 h-5 w-5 text-rose-500" />
                                    <div>
                                        <p className="text-sm font-semibold text-rose-800 dark:text-rose-300">Cancellation Reason</p>
                                        <p className="mt-1 text-sm text-rose-700 dark:text-rose-400">{trip.cancellationReason}</p>
                                        {trip.cancelledBy && (
                                            <p className="mt-2 text-xs text-rose-500">Cancelled by: {trip.cancelledBy}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Booking Info */}
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Booking</h2>
                        </div>
                        <div className="space-y-3 p-6">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-slate-500 dark:text-slate-400">Booking ID</span>
                                <button onClick={() => navigate(`/ride-bookings-trips/bookings/${trip.booking.bookingId}`)} className="font-mono text-sm font-medium text-primary-600 hover:underline">
                                    {trip.booking.bookingId.slice(0, 8)}...
                                </button>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-slate-500 dark:text-slate-400">Seats</span>
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{trip.booking.numberOfSeats}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-slate-500 dark:text-slate-400">Amount</span>
                                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">{formatCurrency(trip.booking.totalAmount, trip.booking.currency)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-slate-500 dark:text-slate-400">Status</span>
                                <StatusBadge status={trip.booking.status} />
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-slate-500 dark:text-slate-400">Payment</span>
                                <StatusBadge status={trip.booking.paymentStatus} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-4">
                    {/* Passenger Card */}
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Passenger</h2>
                        </div>
                        <div className="space-y-3 p-6">
                            <div className="flex items-center gap-3">
                                <UserCircleIcon className="h-5 w-5 text-slate-400" />
                                <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{trip.passenger.firstName} {trip.passenger.lastName}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <EnvelopeIcon className="h-5 w-5 text-slate-400" />
                                <span className="text-sm text-slate-600 dark:text-slate-400">{trip.passenger.email}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <PhoneIcon className="h-5 w-5 text-slate-400" />
                                <span className="text-sm text-slate-600 dark:text-slate-400">{trip.passenger.phone}</span>
                            </div>
                            <Button variant="ghost" size="sm" className="mt-2 w-full text-primary-600" onClick={() => navigate(`/riders/${trip.passenger.userId}`)}>
                                View Rider Profile →
                            </Button>
                        </div>
                    </div>

                    {/* Driver Card */}
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Driver</h2>
                        </div>
                        <div className="space-y-3 p-6">
                            <div className="flex items-center gap-3">
                                <UserCircleIcon className="h-5 w-5 text-slate-400" />
                                <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{trip.driver.firstName} {trip.driver.lastName}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <EnvelopeIcon className="h-5 w-5 text-slate-400" />
                                <span className="text-sm text-slate-600 dark:text-slate-400">{trip.driver.email}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <PhoneIcon className="h-5 w-5 text-slate-400" />
                                <span className="text-sm text-slate-600 dark:text-slate-400">{trip.driver.phone}</span>
                            </div>
                            <Button variant="ghost" size="sm" className="mt-2 w-full text-primary-600" onClick={() => navigate(`/drivers/${trip.driver.userId}`)}>
                                View Driver Profile →
                            </Button>
                        </div>
                    </div>

                    {/* Ride Offer Card */}
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Ride Offer</h2>
                        </div>
                        <div className="space-y-3 p-6">
                            <div className="flex items-center gap-3">
                                <TruckIcon className="h-5 w-5 text-slate-400" />
                                <span className="text-sm text-slate-600 dark:text-slate-400">{trip.rideOffer.vehicleType}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <MapPinIcon className="h-5 w-5 text-emerald-500" />
                                <span className="text-sm text-slate-600 dark:text-slate-400">{trip.rideOffer.origin}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <MapPinIcon className="h-5 w-5 text-rose-500" />
                                <span className="text-sm text-slate-600 dark:text-slate-400">{trip.rideOffer.destination}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <CalendarIcon className="h-5 w-5 text-slate-400" />
                                <span className="text-sm text-slate-600 dark:text-slate-400">{formatDate(trip.rideOffer.departureTime)}</span>
                            </div>
                            <Button variant="ghost" size="sm" className="mt-2 w-full text-primary-600" onClick={() => navigate(`/ride-bookings-trips/rides/${trip.rideOffer.rideOfferId}`)}>
                                View Ride Offer →
                            </Button>
                        </div>
                    </div>

                    {/* Timeline */}
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Timeline</h2>
                        </div>
                        <div className="space-y-0 p-6">
                            <TimelineEvent label="Created" time={trip.timeline.created} isActive={!!trip.timeline.created} />
                            <TimelineEvent label="Started" time={trip.timeline.started} isActive={!!trip.timeline.started} />
                            <TimelineEvent label="Driver Arrived" time={trip.timeline.driverArrived} isActive={!!trip.timeline.driverArrived} />
                            <TimelineEvent label="Picked Up" time={trip.timeline.pickedUp} isActive={!!trip.timeline.pickedUp} />
                            {trip.timeline.completed && (
                                <TimelineEvent label="Completed" time={trip.timeline.completed} isLast={!trip.timeline.cancelled} isActive />
                            )}
                            {trip.timeline.cancelled && (
                                <TimelineEvent label="Cancelled" time={trip.timeline.cancelled} isLast isActive />
                            )}
                            {!trip.timeline.completed && !trip.timeline.cancelled && (
                                <TimelineEvent label={trip.status === "CANCELLED" ? "Cancelled" : "Awaiting..."} time={null} isLast isActive={false} />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Cancel Modal */}
            {cancelModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => { setCancelModal(false); setActionReason(""); }}>
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Cancel Trip</h3>
                        <p className="mt-2 text-sm text-slate-500">This will cancel the active trip.</p>
                        <textarea rows={3} placeholder="Reason..." value={actionReason} onChange={(e) => setActionReason(e.target.value)}
                            className="mt-4 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        />
                        <div className="mt-4 flex justify-end gap-3">
                            <Button variant="outline" size="sm" onClick={() => { setCancelModal(false); setActionReason(""); }}>Go Back</Button>
                            <Button size="sm" className="bg-rose-600 hover:bg-rose-700" disabled={!actionReason.trim() || cancelMutation.isPending} onClick={() => cancelMutation.mutate(actionReason)}>
                                {cancelMutation.isPending ? "Cancelling..." : "Cancel Trip"}
                            </Button>
                        </div>
                        {cancelMutation.isError && <p className="mt-3 text-sm text-rose-600">{extractApiError(cancelMutation.error)}</p>}
                    </div>
                </div>
            )}

            {/* Force Complete Modal */}
            {completeModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => { setCompleteModal(false); setActionReason(""); setFinalFare(""); }}>
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Force Complete Trip</h3>
                        <p className="mt-2 text-sm text-slate-500">Estimated fare: <span className="font-semibold">{formatCurrency(trip.estimatedFare, trip.estimatedFareCurrency)}</span></p>
                        <div className="mt-4 space-y-3">
                            <div>
                                <label className="text-xs font-medium text-slate-500">Final Fare (leave empty to use estimated)</label>
                                <input type="number" placeholder={trip.estimatedFare.toString()} value={finalFare} onChange={(e) => setFinalFare(e.target.value)}
                                    className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                                />
                            </div>
                            <textarea rows={3} placeholder="Reason..." value={actionReason} onChange={(e) => setActionReason(e.target.value)}
                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                            />
                        </div>
                        <div className="mt-4 flex justify-end gap-3">
                            <Button variant="outline" size="sm" onClick={() => { setCompleteModal(false); setActionReason(""); setFinalFare(""); }}>Go Back</Button>
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={completeMutation.isPending}
                                onClick={() => completeMutation.mutate({ reason: actionReason, fare: finalFare ? parseFloat(finalFare) : undefined })}
                            >
                                {completeMutation.isPending ? "Completing..." : "Force Complete"}
                            </Button>
                        </div>
                        {completeMutation.isError && <p className="mt-3 text-sm text-rose-600">{extractApiError(completeMutation.error)}</p>}
                    </div>
                </div>
            )}
        </div>
    );
};
